/**
 * 版本检查工具
 * 用于定期检查应用是否有新版本，并提示用户刷新
 */

import { toast } from 'sonner';
import { addMsgOnReloaded } from './msgOnReload';

interface VersionInfo {
  version: string;
  timestamp: number;
  buildTime: string;
}

// 全局存储当前版本号
let currentVersion: string | null = null;
let lastNotifyId: string | number | undefined;

// 版本检查节流间隔（毫秒），默认 10 秒钟
const DEFAULT_THROTTLE_INTERVAL = 10 * 1000;

// 节流状态
let lastCheckTime = 0;
let lastCheckResult = false;

/**
 * 获取构建时的版本信息
 * 该版本号在构建时被打包进应用代码
 */
async function getBuildVersion() {
  try {
    // 动态导入构建时生成的版本常量
    const { APP_VERSION } = await import('@/constants/version');
    return APP_VERSION;
  } catch {
    console.warn('⚠️ 无法获取构建版本信息');
    return null;
  }
}

/**
 * 初始化版本检查
 * 从服务器获取当前版本号并存储
 */
export async function initVersionCheck(): Promise<void> {
  try {
    const response = await fetch('/version.json?t=' + Date.now());
    const data: VersionInfo = await response.json();
    currentVersion = data.version;
    console.log(`📦 当前应用版本: v${currentVersion}`);
  } catch (error) {
    // 如果无法从服务器获取，尝试使用构建时的版本
    console.warn('⚠️ 无法从服务器获取版本信息，使用构建版本');
    const buildVersion = await getBuildVersion();
    if (buildVersion !== null) {
      currentVersion = buildVersion;
      console.log(`📦 当前应用版本（构建时）: v${currentVersion}`);
    }
  }
}

/**
 * 检查是否有新版本
 * @param throttleMs 节流间隔（毫秒）
 */
export async function checkForUpdate(
  throttleMs: number = DEFAULT_THROTTLE_INTERVAL
): Promise<boolean> {
  // 节流：间隔内直接返回上次结果
  const now = Date.now();
  if (now - lastCheckTime < throttleMs) {
    return lastCheckResult;
  }
  lastCheckTime = now;

  try {
    // 加时间戳防止 version.json 被缓存
    const response = await fetch('/version.json?t=' + Date.now());
    const data: VersionInfo = await response.json();

    // 如果当前版本未初始化，先初始化
    if (currentVersion === null) {
      currentVersion = data.version;
      lastCheckResult = false;
      return false;
    }

    // 比较版本号
    lastCheckResult = data.version !== currentVersion;
    if (lastCheckResult) {
      console.log(`🎉 发现新版本: v${data.version} (当前: v${currentVersion})`);
    }

    return lastCheckResult;
  } catch (error) {
    console.warn('⚠️ 版本检查失败:', error);
    lastCheckResult = false;
    // 检查失败时不保留节流时间，允许下次立即重试
    lastCheckTime = 0;
    return false;
  }
}

/**
 * 通知用户已更新
 */
export function notifyUpdate(): void {
  if (lastNotifyId) {
    toast.dismiss(lastNotifyId);
  }
  lastNotifyId = toast.success('发现新版本', {
    description: '检测到新版本，点击"立即更新"刷新页面',
    duration: Infinity,
    action: {
      label: '立即更新',
      onClick: () => {
        addMsgOnReloaded({
          type: 'success',
          content: '已更新到新版本',
        });
        window.location.reload();
      },
    },
  });
}

/**
 * 启动版本轮询检查
 * @param interval 检查间隔（毫秒），默认 5 分钟
 * @param autoPrompt 是否自动提示用户刷新，默认 true
 */
export function startVersionPolling(
  interval: number = 5 * 60 * 1000,
  autoPrompt: boolean = true
): () => void {
  console.log(`🔄 已启动版本轮询检查，间隔: ${interval / 1000}秒`);

  const checkUpdate = async () => {
    const hasUpdate = await checkForUpdate();
    if (hasUpdate && autoPrompt) {
      notifyUpdate();
    }
    console.log('检测更新结果：', hasUpdate);
  };

  // 立即执行一次检查
  checkUpdate();

  // 设置定时轮询
  const timerId = setInterval(checkUpdate, interval);

  // 返回清理函数
  return () => {
    clearInterval(timerId);
    console.log('⏹️ 版本轮询检查已停止');
  };
}

/**
 * 手动检查并提示更新
 */
export async function manualCheckUpdate(): Promise<void> {
  const hasUpdate = await checkForUpdate();
  if (hasUpdate) {
    notifyUpdate();
  } else {
    console.log('✅ 当前已是最新版本');
  }
}

/**
 * 页面回到前台时立即检查一次更新
 */
export function checkOnVisible(): () => void {
  const onVisibilityChange = () => {
    if (!document.hidden) manualCheckUpdate();
  };

  const onPageShow = (e: PageTransitionEvent) => {
    if (e.persisted) manualCheckUpdate();
  };

  const onFocus = () => manualCheckUpdate();

  if (typeof document.hidden !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('focus', onFocus);

  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pageshow', onPageShow);
    window.removeEventListener('focus', onFocus);
  };
}
